import { PlusCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Input, Splitter } from 'antd';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useParams, useNavigate } from 'react-router';
import slugify from 'slugify';
import { useGetMpCategories } from '../../../../utils/api/mpCategories/useGetMpCategories';
import AddCategories from './add';
import MarketplaceCategoryDetails from './details';
import './marketplace-categories.css';
import MarketplaceCategoriesTree from './MarketplaceCategoriesTree';

const MarketplaceCategories = () => {
  const navigate = useNavigate();

  const { marketplace, category } = useParams();
  const [search, setSearch] = useState('');
  const [localSearch, setLocalSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [sizes, setSizes] = useState(['30%', '70%']);
  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false);
  const debounceRef = useRef(null);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setLocalSearch(value);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setSearch(value);
    }, 300);
  };

  const handleCategorySelect = useCallback(
    (category, info) => {
      if (category && info.selected) {
        const categorySlug = slugify(category.label, {
          lower: true,
          strict: true,
        });
        setSelectedCategory(category);
        navigate(
          `/dashboard/marketplaces/${marketplace}/categories/${categorySlug}/category-attributes`
        );
      } else {
        setSelectedCategory(null);
      }
    },
    [navigate, marketplace]
  );

  const categoryParams = useMemo(
    () => ({
      marketplace__name: marketplace,
      without_attributes: true,
      limit: 1000,
      ...(search.trim() ? { search: search.trim() } : {}),
    }),
    [marketplace, search]
  );

  const { mpCategories, isLoading } = useGetMpCategories(categoryParams, {
    enabled: !!marketplace,
  });

  const filteredCategories = useMemo(() => {
    if (!mpCategories || mpCategories.length === 0) return [];

    return mpCategories.map((cat) => ({
      key: cat.id,
      id: cat.id,
      name: cat.name,
      label: cat.label || cat.name,
      marketplace: cat.marketplace,
      attributesCount: cat.attributes?.length || 0,
    }));
  }, [mpCategories]);

  useEffect(() => {
    if (!isLoading) {
      const defaultSelectedCategory =
        filteredCategories.find(
          (c) =>
            slugify(c.label || '', { lower: true, strict: true }) === category
        ) || null;
      setSelectedCategory(defaultSelectedCategory);
    }
  }, [isLoading, filteredCategories, category]);

  return (
    <div
      style={{
        height: 'calc(100vh - 120px)',
        display: 'flex',
        flexDirection: 'column',
      }}
      className="marketplace-categories-container"
    >
      <Splitter onResize={setSizes} style={{ height: '100%' }}>
        <Splitter.Panel size={sizes[0]} resizable={true}>
          <div
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              padding: '16px',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                justifyContent: 'space-between',
              }}
            >
              <h2 style={{ margin: '0 0 14px 0' }}>Catégories {marketplace}</h2>

              {marketplace !== 'shoppingfeed' && (
                <Button
                  type="primary"
                  size="small"
                  style={{ margin: 0 }}
                  icon={<PlusCircleOutlined />}
                  onClick={() => setIsAddDrawerOpen(true)}
                >
                  Ajouter une catégorie
                </Button>
              )}
            </div>
            <div style={{ marginTop: '16px' }}>
              <Input
                value={localSearch}
                placeholder="Rechercher une catégorie marketplace"
                prefix={<SearchOutlined />}
                onChange={handleSearchChange}
                allowClear
              />
            </div>
            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                minHeight: 0,
                padding: '16px',
              }}
            >
              <MarketplaceCategoriesTree
                categories={filteredCategories}
                onSelect={handleCategorySelect}
                loading={isLoading}
              />
            </div>
          </div>
        </Splitter.Panel>
        <Splitter.Panel size={sizes[1]}>
          <div
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              overflowY: 'auto',
            }}
          >
            <div style={{ padding: '16px' }}>
              <MarketplaceCategoryDetails category={selectedCategory} />
            </div>
          </div>
        </Splitter.Panel>
      </Splitter>
      <AddCategories
        open={isAddDrawerOpen}
        mpCategories={mpCategories}
        onClose={() => setIsAddDrawerOpen(false)}
      />
    </div>
  );
};

export default MarketplaceCategories;
